import React, { useState } from 'react';
import { Grid, Typography } from '@material-ui/core';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import clsx from 'clsx';

import BaseButton from 'features/app/components/BaseButton';
import useAuth from 'features/auth/hooks/useAuth';
import { useStyles } from 'features/get-started/components/styles';
import { useTranslation } from 'hooks/useTranslation';
import useGetStarted from 'features/get-started/hooks/useGetStarted';
import { TopUpBalanceForm } from 'features/finance/components/TopUpBalanceForm';
import { StripePublicKey } from 'features/finance/constants/stripe-public-key';
import { queryClient } from 'api/query';
import { SuccessfulAction } from 'features/dashboard/components/SuccessfulAction';
import { FailedAction } from 'features/dashboard/components/FailedAction';
import { initialTopUpStatusState, TopUpStatus } from 'features/finance/components/TopUpBalanceModal';
import { getFormattedAmountWithCurrency } from 'features/finance/utils/get-formatted-amount-with-currency';
import { Loaders } from 'shared';
import { ReactComponent as SuccessfulTopUpIcon } from 'assets/icons/successful-top-up-icon.svg';
import i18n from 'locales/i18n';

const stripePromise = loadStripe(StripePublicKey);

export const SecondStep = () => {
  const { user } = useAuth();
  const direction = i18n.dir();

  const [topUpStatus, setTopUpStatus] = useState<TopUpStatus>(initialTopUpStatusState);
  const classes = useStyles({ direction, topUpSuccess: topUpStatus.type === 'success' });
  const { t } = useTranslation('get-started');
  const { backToDashboard } = useGetStarted();
  const [isTopUpBalanceFormShown, setIsTopUpBalanceFormShown] = useState(false);

  const showTopUpBalanceForm = () => {
    !isTopUpBalanceFormShown && setIsTopUpBalanceFormShown(true);
  };

  const onSuccessfulAction = async () => {
    await queryClient.invalidateQueries('partner-balance');
    await queryClient.invalidateQueries('top-up-history');
  };

  const onProcessing = () => {
    setTopUpStatus({
      isModalShown: false,
      type: 'processing',
    });
  };

  const onTopUpSuccess = (amount: number, currency: string) => {
    setTopUpStatus({
      isModalShown: true,
      type: 'success',
      amount: amount,
      currency: currency,
    });
  };

  const onTopUpFailed = (message?: string) => {
    setTopUpStatus({
      isModalShown: true,
      message: message || '',
      type: 'fail',
    });
  };

  const ResponsePopup = () => {
    return topUpStatus.type === 'success' ? (
      <SuccessfulAction
        icon={SuccessfulTopUpIcon}
        onSuccessfulAction={onSuccessfulAction}
        title={`${getFormattedAmountWithCurrency(topUpStatus.amount as number, topUpStatus.currency as string)} \n ${t(
          'topUpSuccessful'
        )}`}
      />
    ) : (
      <FailedAction error={topUpStatus.message} />
    );
  };

  return (
    <Elements stripe={stripePromise}>
      <Grid container justifyContent={'space-between'} className={classes.root} wrap={'nowrap'}>
        <Grid className={classes.leftBlockWrapper}>
          <Typography className={classes.stepTitle}>{t('step')} 2/2</Typography>
          <Typography className={classes.name}>
            {t('hi')} <br />
            {user?.first_name} {user?.last_name}
          </Typography>

          <Typography className={classes.title} variant={'h2'}>
            {t('addFunds')}
          </Typography>
          <Typography className={classes.subtitle}>{t('setupWallets')}</Typography>

          <Grid container wrap={'nowrap'}>
            <BaseButton className={classes.skipBtn} variant={'text'} onClick={backToDashboard}>
              {t('skip')}
            </BaseButton>
            <BaseButton className={classes.skipAndNextBtn} onClick={backToDashboard}>
              {t('skipAndNext')}
            </BaseButton>
          </Grid>
        </Grid>

        {topUpStatus.isModalShown ? (
          <ResponsePopup />
        ) : isTopUpBalanceFormShown ? (
          <Grid className={classes.topUpBalanceForm}>
            <Typography className={classes.topUpBalanceTitle}>{t('topUpBalance')}</Typography>

            {topUpStatus.type === 'processing' ? (
              <Loaders.Loader />
            ) : (
              <TopUpBalanceForm
                onProcessing={onProcessing}
                onTopUpSuccess={onTopUpSuccess}
                onTopUpFailed={onTopUpFailed}
              />
            )}
          </Grid>
        ) : (
          <Grid className={classes.chooseHowTopUpBalanceBlock}>
            <Typography className={clsx(classes.title, classes.topUpTitle)} variant={'h2'}>
              {t('chooseHowToTopUpBalance')}
            </Typography>

            <Grid container className={classes.topUpWrapper} alignItems={'center'} justifyContent={'space-between'}>
              <div>
                <Typography className={classes.topUpText}>{t('account')}</Typography>
                <Typography className={classes.topUpName}>
                  {user?.first_name} {user?.last_name}
                </Typography>
                <Typography className={classes.topUpText}>{t('meritIncentives')}</Typography>
              </div>

              <div>
                <div className={classes.topUpWindow} />
              </div>

              <BaseButton
                variant={'outlined'}
                onClick={showTopUpBalanceForm}
                maxWidth={350}
                className={classes.topUpButton}
              >
                + {t('topUpBalance')}
              </BaseButton>
            </Grid>

            <Grid className={classes.dividerBlock}>
              <Typography className={classes.divider}>Or</Typography>
            </Grid>

            <Grid className={classes.bankTransferBlock}>
              <Typography className={classes.bankTransferBlockText}>
                {t('sendUsAnEmailTo')}
                <span className={classes.email}> care@rewardsby.com </span>
                {t('weWillGladAssistYou')}
              </Typography>
            </Grid>
          </Grid>
        )}
      </Grid>
    </Elements>
  );
};
